/*
 * <recipe-article> — enhances a recipe blog article into a best-in-class page.
 *
 * It parses the (semi-structured) article body into a two-column
 * Ingredients | Method layout, adds tap-to-check ingredients, a Cook Mode
 * (screen wake-lock + step highlighting), a working add-to-cart, a sticky
 * mobile buy bar, and injects Recipe JSON-LD for search engines.
 *
 * Everything degrades gracefully: with JS off, the original body still shows.
 */
class RecipeArticle extends HTMLElement {
  connectedCallback() {
    this.source = this.querySelector('[data-recipe-source]');
    this.grid = this.querySelector('[data-recipe-grid]');
    try {
      this.build();
    } catch (e) {
      /* Leave the raw content visible if parsing fails. */
      if (window.console) console.warn('recipe-article: parse failed', e);
    }
    this.wireTools();
    this.wireCart();
    this.wireBuyBar();
  }

  /* ---- Parse + render structured layout ---- */
  build() {
    if (!this.source || !this.grid) return;

    // Clean editor cruft.
    this.source.querySelectorAll('meta, img, br').forEach((n) => n.remove());

    const blocks = [...this.source.children];
    if (blocks.length === 0) return;

    const ingList = this.grid.querySelector('[data-ingredients-list]');
    const methodList = this.grid.querySelector('[data-method-list]');

    // Split content by the "Directions/Method" marker in document order —
    // robust whether steps are in <ol>, <ul>, or <p> (Canva pastes vary).
    const markerText = (el) => this.clean(el.textContent);
    const dirRe = /^(directions?|method|instructions?|steps?|preparation)\b/i;
    const ingRe = /^ingredients?\b/i;
    const dirIdx = blocks.findIndex(
      (el) => markerText(el).length <= 42 && dirRe.test(markerText(el)) && !ingRe.test(markerText(el))
    );

    let ingZone;
    let methodZone;
    if (dirIdx !== -1) {
      let startIdx = blocks.findIndex((el) => markerText(el).length <= 42 && ingRe.test(markerText(el)));
      if (startIdx === -1 || startIdx > dirIdx) startIdx = -1;
      ingZone = blocks.slice(startIdx + 1, dirIdx);
      methodZone = blocks.slice(dirIdx + 1);
    } else {
      ingZone = blocks.filter((el) => el.tagName === 'UL');
      methodZone = blocks.filter((el) => el.tagName === 'OL');
    }

    const ingredientCount = this.renderIngredients(ingZone, ingList);
    const stepsEl = this.renderMethod(methodZone, methodList);
    const stepCount = stepsEl.querySelectorAll('.recipe-step').length;

    if (ingredientCount === 0 || stepCount === 0) {
      // Not confidently structured — keep the original body visible.
      ingList.innerHTML = '';
      methodList.innerHTML = '';
      return;
    }

    this.stats = this.extractStats(this.source.textContent);
    this.renderStats(this.stats, stepCount, ingList);

    this.grid.hidden = false;
    this.source.hidden = true;

    this.injectJsonLd(ingList, stepsEl);
    this.wireSteps(stepsEl);
  }

  renderIngredients(zone, container) {
    let count = 0;
    zone.forEach((el) => {
      if (el.tagName === 'UL' || el.tagName === 'OL') {
        el.querySelectorAll('li').forEach((li) => {
          const text = this.clean(li.textContent);
          if (!text) return;
          count += 1;
          const lbl = document.createElement('label');
          lbl.className = 'recipe-ingredient';
          const cb = document.createElement('input');
          cb.type = 'checkbox';
          const span = document.createElement('span');
          span.textContent = text;
          lbl.appendChild(cb);
          lbl.appendChild(span);
          container.appendChild(lbl);
        });
      } else {
        const t = this.clean(el.textContent);
        if (t && t.length <= 42 && /[:：]$/.test(t) && !/ingredient/i.test(t)) {
          const h = document.createElement('p');
          h.className = 'recipe-ingredients__subhead';
          h.textContent = t.replace(/[:：]\s*$/, '');
          container.appendChild(h);
        }
      }
    });
    return count;
  }

  renderMethod(zone, container) {
    const steps = document.createElement('ol');
    steps.className = 'recipe-steps';
    let n = 0;
    const addStep = (raw) => {
      const text = this.stripNum(this.clean(raw));
      if (!text || text.length < 2) return;
      n += 1;
      const item = document.createElement('li');
      item.className = 'recipe-step';
      const num = document.createElement('span');
      num.className = 'recipe-step__num';
      const numText = document.createElement('span');
      numText.className = 'recipe-step__num-text';
      numText.textContent = n;
      num.appendChild(numText);
      const body = document.createElement('div');
      body.className = 'recipe-step__text';
      body.textContent = text;
      item.appendChild(num);
      item.appendChild(body);
      steps.appendChild(item);
    };

    const liEls = [];
    zone.forEach((el) => liEls.push(...el.querySelectorAll('li')));
    if (liEls.length) {
      liEls.forEach((li) => addStep(li.textContent));
    } else {
      const paras = [];
      zone.forEach((el) => {
        const t = this.clean(el.textContent);
        if (!t) return;
        if (t.length <= 42 && /[:：]$/.test(t)) return; // sub-label only
        paras.push(t);
      });
      if (paras.length === 1) {
        this.splitSentences(paras[0]).forEach(addStep);
      } else {
        paras.forEach(addStep);
      }
    }
    container.appendChild(steps);
    return steps;
  }

  stripNum(t) {
    return (t || '').replace(/^\s*(?:step\s*)?\d+\s*[.):\-]\s*/i, '').trim();
  }

  splitSentences(text) {
    return text
      .split(/(?<=[.!?])\s+(?=[A-Z0-9"'])/)
      .map((s) => s.trim())
      .filter((s) => s.length > 1);
  }

  clean(str) {
    return (str || '')
      .replace(/�/g, '')
      .replace(/\s+/g, ' ')
      .replace(/\s*\?\s*$/, '')
      .trim();
  }

  extractStats(text) {
    const t = (text || '').replace(/ /g, ' ');
    const stats = {};
    const dur = '([\\d][^\\n<]*?(?:hours?|hrs?|minutes?|mins?))';
    const grab = (re) => {
      const m = t.match(re);
      return m ? this.clean(m[1]) : null;
    };
    stats.prep = grab(new RegExp('prep(?:\\s*time)?\\s*[:\\-]?\\s*' + dur, 'i'));
    stats.cook = grab(new RegExp('cook(?:ing)?\\s*time\\s*[:\\-]?\\s*' + dur, 'i'));
    stats.total = grab(new RegExp('total\\s*time\\s*[:\\-]?\\s*' + dur, 'i'));
    const y = t.match(/(?:serves|servings|yields?|makes)\s*[:\-]?\s*([0-9]+(?:\s*[-–]\s*[0-9]+)?)/i);
    if (y) stats.yield = this.clean(y[1]);
    return stats;
  }

  renderStats(stats, stepCount, ingList) {
    const host = this.querySelector('[data-recipe-stats]');
    if (!host) return;
    const add = (value, label) => {
      if (!value) return;
      const wrap = document.createElement('div');
      wrap.className = 'recipe-article__stat';
      wrap.innerHTML =
        '<span class="recipe-article__stat-value"></span><span class="recipe-article__stat-label"></span>';
      wrap.querySelector('.recipe-article__stat-value').textContent = value;
      wrap.querySelector('.recipe-article__stat-label').textContent = label;
      host.appendChild(wrap);
    };
    add(stats.prep, 'Prep');
    add(stats.cook, 'Cook time');
    add(stats.total, 'Total');
    add(stats.yield, 'Serves');
    const ing = ingList.querySelectorAll('.recipe-ingredient').length;
    if (ing) add(ing, ing === 1 ? 'Ingredient' : 'Ingredients');
    if (stepCount) add(stepCount, stepCount === 1 ? 'Step' : 'Steps');
  }

  toIso(str) {
    if (!str) return null;
    const h = str.match(/(\d+)\s*(?:h|hour|hr)/i);
    const mn = str.match(/(\d+)\s*(?:m|min)/i);
    if (!h && !mn) return null;
    return 'PT' + (h ? h[1] + 'H' : '') + (mn ? mn[1] + 'M' : '');
  }

  injectJsonLd(ingList, steps) {
    const d = this.dataset;
    const ingredients = [...ingList.querySelectorAll('.recipe-ingredient span')].map((s) =>
      s.textContent.trim()
    );
    const instructions = [...steps.querySelectorAll('.recipe-step__text')].map((s) => ({
      '@type': 'HowToStep',
      text: s.textContent.trim(),
    }));
    const data = {
      '@context': 'https://schema.org/',
      '@type': 'Recipe',
      name: d.title,
      author: { '@type': d.author ? 'Person' : 'Organization', name: d.author || 'Besto' },
    };
    if (d.image) data.image = [d.image];
    if (d.published) data.datePublished = d.published;
    if (d.category) data.recipeCategory = d.category;
    if (d.keywords) data.keywords = d.keywords;
    if (this.stats && this.stats.yield) data.recipeYield = this.stats.yield;
    const iso = this.stats ? this.toIso(this.stats.cook) : null;
    if (iso) data.totalTime = iso;
    if (ingredients.length) data.recipeIngredient = ingredients;
    if (instructions.length) data.recipeInstructions = instructions;

    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.textContent = JSON.stringify(data);
    document.head.appendChild(tag);
  }

  /* ---- Cook mode / step check-off ---- */
  wireSteps(steps) {
    steps.querySelectorAll('.recipe-step').forEach((step) => {
      step.addEventListener('click', () => {
        if (!this.classList.contains('is-cook-mode')) return;
        step.classList.toggle('is-done');
      });
    });
  }

  wireTools() {
    const cook = this.querySelector('[data-cook-mode]');
    if (cook) {
      cook.addEventListener('click', () => this.toggleCookMode(cook));
    }
    const exit = this.querySelector('[data-cook-mode-exit]');
    if (exit && cook) {
      exit.addEventListener('click', () => this.toggleCookMode(cook, false));
    }
    const print = this.querySelector('[data-print]');
    if (print) print.addEventListener('click', () => window.print());

    const share = this.querySelector('[data-share]');
    if (share) {
      share.addEventListener('click', async () => {
        const url = share.dataset.shareUrl;
        const title = share.dataset.shareTitle;
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (e) {
            /* user cancelled */
          }
        } else if (navigator.clipboard) {
          await navigator.clipboard.writeText(url);
          const label = share.querySelector('span');
          if (label) {
            const prev = label.textContent;
            label.textContent = 'Link copied';
            setTimeout(() => (label.textContent = prev), 1600);
          }
        }
      });
    }
  }

  async toggleCookMode(btn, force) {
    const on = typeof force === 'boolean' ? force : !this.classList.contains('is-cook-mode');
    this.classList.toggle('is-cook-mode', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');

    const bar = this.querySelector('[data-cookmode-bar]');
    if (bar) {
      if (on) {
        bar.hidden = false;
        requestAnimationFrame(() => bar.classList.add('is-visible'));
      } else {
        bar.classList.remove('is-visible');
      }
    }

    if (on) {
      const first = this.querySelector('.recipe-step');
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        if ('wakeLock' in navigator) this._wakeLock = await navigator.wakeLock.request('screen');
      } catch (e) {
        /* wake lock unavailable (e.g. desktop / unsupported) — mode still works */
      }
    } else {
      this.querySelectorAll('.recipe-step.is-done').forEach((s) => s.classList.remove('is-done'));
      if (this._wakeLock) {
        try {
          this._wakeLock.release();
        } catch (e) {
          /* ignore */
        }
        this._wakeLock = null;
      }
    }
  }

  /* ---- Add to cart ---- */
  wireCart() {
    this.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      btn.addEventListener('click', () => this.addToCart(btn));
    });
  }

  async addToCart(btn) {
    const id = btn.dataset.variantId;
    if (!id) return;
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('add failed');
      btn.textContent = 'Added ✓';
      btn.classList.add('is-added');
      this.refreshCartCount();
      setTimeout(() => {
        btn.textContent = original;
        btn.classList.remove('is-added');
        btn.disabled = false;
      }, 2200);
    } catch (e) {
      btn.textContent = original;
      btn.disabled = false;
      window.location.href = this.dataset.productUrl || '/cart';
    }
  }

  async refreshCartCount() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
        el.textContent = cart.item_count;
      });
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {
      /* non-critical */
    }
  }

  /* ---- Sticky mobile buy bar (shows after the buy card scrolls past) ---- */
  wireBuyBar() {
    const bar = this.querySelector('[data-recipe-buybar]');
    const anchor = this.querySelector('[data-recipe-buy]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.hidden = false;
          bar.classList.toggle('is-visible', show);
        });
      },
      { threshold: 0 }
    );
    io.observe(anchor);
  }
}

if (!customElements.get('recipe-article')) {
  customElements.define('recipe-article', RecipeArticle);
}
